import { CommonModule, DatePipe, formatDate } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  Injector,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventServiceRepository, toEventDetails } from '@softapps/core/events';
import {
  BackNavigable,
  BackNavigationService,
  RefreshService,
  Refreshable,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
  handleRequestError,
  menuItems,
} from '@softline/application';
import { Store } from '@softline/core';
import {
  Subscription,
  combineLatest,
  distinctUntilChanged,
  map,
  switchMap,
  startWith,
} from 'rxjs';
import { SecondsToHoursPipe } from './../../pipes/seconds-to-hours.pipe';
import { DateNavigatorService } from './../../services/date-navigator.service';
import { SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL } from './../../statusabfrage.shared';
import { PmsPersonalDetailStore } from './../../store/personal-detail.store';
import { Termin } from '../../types/mitarbeiter';

import { AccordionComponent } from '@softline/ui-core';
import { AccordionItemComponent } from '@softline/ui-core';
import { AccordionItemContentDirective } from '@softline/ui-core';
import { AccordionItemHeaderDirective } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';

@Component({
  selector: 'soft-mitarbeiter-detail',
  imports: [
    CommonModule,
    SecondsToHoursPipe,
    AccordionComponent,
    AccordionItemComponent,
    AccordionItemHeaderDirective,
    FormatPipe,
    IconComponent,
    RepeatDirective,
    TranslatePipe,
    AccordionItemContentDirective,
  ],
  templateUrl: './mitarbeiter-detail.component.html',
  styleUrls: ['./mitarbeiter-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe, EventServiceRepository],
})
export class MitarbeiterDetailComponent
  implements OnInit, OnDestroy, BackNavigable, Refreshable
{
  readonly expanded = true;
  private mitarbeiterSubscription?: Subscription;
  private subscription?: Subscription;

  private readonly id$ = this.activatedRoute.paramMap.pipe(map((o) => o.get('idlstfix')));

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
    PmsPersonalDetailStore.getters.loading
  );

  readonly mitarbeiter$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
    PmsPersonalDetailStore.getters.data
  );

  readonly mitarbeiterDetail$ = this.id$.pipe(
    switchMap((id) =>
      this.store.observe(
        SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
        PmsPersonalDetailStore.getters.data
      )
    )
  );

  private readonly injector = inject(Injector);
  private readonly eventServiceRepository = inject(EventServiceRepository);
  private readonly dateNavigator = inject(DateNavigatorService);

  private readonly externalTermine$ = combineLatest([
    this.mitarbeiter$.pipe(startWith(null)),
    this.dateNavigator.date$,
  ]).pipe(
    map(([detail, date]) => ({
      mail: detail?.adresse?.mail || null,
      day: date ? formatDate(date, 'yyyy-MM-dd', 'en-US') : null,
    })),
    distinctUntilChanged((a, b) => a?.mail === b?.mail && a?.day === b?.day),
    switchMap(({ mail, day }) => this.loadExternalTermine(mail, day))
  );

  readonly termine$ = combineLatest([
    this.mitarbeiter$.pipe(startWith(null)),
    this.externalTermine$.pipe(startWith([])),
  ]).pipe(
    map(([detail, external]) =>
      [...(detail?.termine ?? []), ...external].sort(
        (a, b) => new Date(a.vonzeit).getTime() - new Date(b.vonzeit).getTime()
      )
    )
  );

  private async loadExternalTermine(
    mail: string | null,
    day: string | null
  ): Promise<Termin[]> {
    if (!mail || !day) return [];
    try {
      const events = await this.eventServiceRepository.load({
        // local day bounds as ISO-UTC for the event-service query
        from: new Date(`${day}T00:00:00`).toISOString(),
        to: new Date(`${day}T23:59:59.999`).toISOString(),
        user: mail,
      });
      return (events ?? []).map((event) => {
        const details = toEventDetails(event);
        return {
          vonzeit: details.start,
          biszeit: details.end,
          taetigkeit: event.subject,
          external: true,
        };
      });
    } catch (e) {
      handleRequestError(e, this.injector, { show: false });
      return [];
    }
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private readonly store: Store,
    private datePipe: DatePipe,
    private readonly dateNavigatorService: DateNavigatorService,
    private readonly backNavigationService: BackNavigationService,
    private readonly refreshService: RefreshService
  ) {
    menuItems(() => [
      {
        name: 'abteilungen',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-list',
        title: 'Abteilungen',
        routerLink: ['/statusabfrage'],
      },
      {
        name: 'abteilung',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-users',
        title: 'Abteilung',
        routerLink: [
          '/statusabfrage',
          'abteilung',
          this.activatedRoute.snapshot.paramMap.get('id'),
        ],
      },
      {
        name: 'mitarbeiter',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-user',
        title: 'Mitarbeiterdetails',
        routerLink: [
          '/statusabfrage',
          'abteilung',
          this.activatedRoute.snapshot.paramMap.get('id'),
          'detail',
          this.activatedRoute.snapshot.paramMap.get('idlstfix'),
        ],
      },
    ]);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['./../../'], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve',
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.refreshService.add(this);

    this.subscription = combineLatest([
      this.id$,
      this.dateNavigatorService.date$,
    ]).subscribe(async ([id, date]) => {
      await this.store.dispatch(
        SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
        PmsPersonalDetailStore.actions.load,
        {
          pathParams: {
            stichdatum: this.datePipe.transform(date, 'yyyy-MM-dd'),
            idlstfix: id,
          },
        }
      );
    });

    this.mitarbeiterSubscription = this.mitarbeiter$.subscribe((mitarbeiter) => {
      if (!mitarbeiter) {
        this.store.commit(
          SOFTLINE_FEATURE_TITLE,
          TitleStore.mutations.setSubTitle,
          `Lade...`
        );
        return;
      }

      const { persnr, vorname, zuname } = mitarbeiter.lstfix;

      this.store.commit(
        SOFTLINE_FEATURE_TITLE,
        TitleStore.mutations.setSubTitle,
        `${persnr} - ${vorname} ${zuname}`
      );
    });
  }

  ngOnDestroy(): void {
    this.backNavigationService.set(undefined);
    this.refreshService.remove(this);
    this.mitarbeiterSubscription?.unsubscribe();
    this.subscription?.unsubscribe();

    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
      PmsPersonalDetailStore.mutations.unset
    );

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');
  }

  async refresh(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
      PmsPersonalDetailStore.actions.load,
      {
        pathParams: {
          stichdatum: this.datePipe.transform(
            this.dateNavigatorService.date,
            'yyyy-MM-dd'
          ),
          idlstfix: this.activatedRoute.snapshot.paramMap.get('idlstfix'),
        },
      }
    );
  }
}
